import * as Sentry from "@sentry/react";
import { EventEmitter } from "../EventEmitter";

export function copyTextToClipboard(copyText: string, message?: string) {
    navigator.clipboard.writeText(copyText).then(() => {
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message: message || "Copied to clipboard",
                type: "info",
            },
        });
    });
}

export function copyUrlToClipboard(path: string, message?: string) {
    const { protocol, host } = window.location;
    copyTextToClipboard(`${protocol}//${host}${path}`, message);
}

export function copyCurrentUrlToClipboard(message?: string) {
    copyTextToClipboard(window.location.toString(), message);
}

export async function readTextFromClipboard(): Promise<string | null> {
    try {
        const text = await navigator.clipboard.readText();
        return text;
    } catch (error) {
        Sentry.captureException(error, {
            extra: {
                message: "Reading text with navigator.clipboard.readText failed",
            },
        });
        return null;
    }
}

export function getUrlPathComponents(possibleUrl: string): string[] | null {
    try {
        const url = new URL(possibleUrl);
        if (url.host === window.location.host) {
            return url.pathname.split("/").filter(Boolean);
        }
        return null;
    } catch {
        return null;
    }
}

export function isMobile() {
    return window.innerWidth < 768;
}

export function scrollRefToTop(
    ref: React.RefObject<HTMLDivElement | null>,
    animate?: boolean,
) {
    if (ref.current) {
        ref.current.scrollTo({
            top: 0,
            behavior: animate ? "smooth" : "instant",
        });
    }
}

export function scrollRefToBottom(
    ref: React.RefObject<HTMLDivElement | null>,
    animate?: boolean,
) {
    if (ref.current) {
        ref.current.scrollTo({
            top: ref.current.scrollHeight,
            behavior: animate ? "smooth" : "instant",
        });
    }
}

export function scrollToElement(
    id: string,
    animate?: boolean,
    center?: boolean,
    callback?: () => void,
) {
    const element = document.getElementById(id);

    if (element) {
        element.scrollIntoView({
            behavior: animate ? "smooth" : "instant",
            ...(center ? { block: "center", inline: "center" } : {}),
        });

        callback?.();
    }
}

export function addCssClass(id: string, className: string) {
    const element = document.getElementById(id);

    if (element) {
        element.classList.add(className);
    }
}

export function removeCssClass(id: string, className: string) {
    const element = document.getElementById(id);

    if (element) {
        element.classList.remove(className);
    }
}

export function revokeCssClass(id: string, className: string) {
    removeCssClass(id, className);
    setTimeout(() => {
        addCssClass(id, className);
    }, 200);
}

export function isElementDescendantOfClassName(
    element: HTMLElement,
    parentClassName: string,
) {
    const parents = document.getElementsByClassName(parentClassName);
    return (
        parents &&
        Array.from(parents)
            .map((parent) => parent.contains(element))
            .filter((it) => it).length !== 0
    );
}

export function logErrorAndAlert(error: Error, message?: string) {
    Sentry.captureException(error, {
        extra: { message: `${message ?? ""} at ${window.location.href}` },
    });
    window.alert(message || error.message);
}

export function logErrorAndShowToast(error: any, message?: string) {
    Sentry.captureException(error, {
        extra: { message: `${message ?? ""} at ${window.location.href}` },
    });
    EventEmitter.emit({
        name: "showToastMessage",
        params: {
            message: message || error.message,
            type: "error",
        },
    });
}

export function stopEnterKeyPress(
    event: React.KeyboardEvent<HTMLInputElement>,
): boolean {
    if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
    return true;
}

export function loadCSS(cssUrl: string): void {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = cssUrl;
    document.head.appendChild(link);
}

export function openLinkInNewTab(url: string) {
    window.open(url, "_blank");
}
